import { Injectable } from '@angular/core';
import { Item } from '@horizon/dm-core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiExceptionsHandlerService } from './api-exceptions-handler.service';
import { DynamicApiService } from './dynamic-api.service';

export type AccountType = 'company' | 'accountPerson';

@Injectable({
  providedIn: 'root',
})
export class AccountDataService {
  public scopeId$ = new BehaviorSubject<number>(0);

  public accountData$ = new BehaviorSubject<any>({});
  public accountBalanceHistory$ = new BehaviorSubject<any>({});
  public accountLESummaryData$ = new BehaviorSubject<any>({});
  public accountType$ = new BehaviorSubject<AccountType>(null);
  public accountTemplate$ = new BehaviorSubject<any>({});
  public mergeAccountData$ = new BehaviorSubject<any>({});
  public accountCaseOwner$ = new BehaviorSubject<string>(undefined);

  public accountRelatedCustomers$ = new BehaviorSubject<Item[]>([]);
  public accountCustomers$ = new BehaviorSubject<Item[]>([]);
  public customerRepresentatives$ = new BehaviorSubject<Item[]>([]);
  public accountRepresentatives$ = new BehaviorSubject<Item[]>([]);
  public accountParticipants$ = new BehaviorSubject<Item[]>([]);
  public customerRepresentativeParties$ = new BehaviorSubject<Item[]>([]);
  public accountRepresentativeParties$ = new BehaviorSubject<Item[]>([]);
  public accountParticipantParties$ = new BehaviorSubject<Item[]>([]);
  public accountAssets$ = new BehaviorSubject<Item[]>([]);
  public accountTreeNodes$ = new BehaviorSubject<any>([]);

  public groupExposureId$ = new BehaviorSubject<number>(0);

  public systemDate$ = new BehaviorSubject<any>({});

  public legalTitleHolder$ = new BehaviorSubject<string>('');

  public companyId$ = new BehaviorSubject<number>(0);

  public accountInBreathingSpace$ = new BehaviorSubject<boolean>(false);

  constructor(
    private apiExceptionsHandlerService: ApiExceptionsHandlerService,
    private dynamicApi: DynamicApiService
  ) {}

  public setScopeId(value: number): void {
    this.scopeId$.next(value);
  }

  public getScopeId(): number {
    return this.scopeId$.getValue();
  }

  public setAccountLESummaryData(value: AccountType): void {
    this.accountLESummaryData$.next(value);
  }

  public getAccountLESummaryData(): any {
    return this.accountLESummaryData$.getValue();
  }

  public getAccountLESummaryDataChanges(): Observable<any> {
    return this.accountLESummaryData$.asObservable();
  }

  public setAccountType(value: AccountType): void {
    this.accountType$.next(value);
  }

  public getAccountType(): any {
    return this.accountType$.getValue();
  }

  public getAccountTypeChanges(): Observable<any> {
    return this.accountType$.asObservable();
  }

  public setAccountData(value: any): void {
    this.accountData$.next(value);
  }

  public getAccountData(): any {
    return this.accountData$.getValue();
  }

  public getAccountDataChanges(): Observable<any> {
    return this.accountData$.asObservable();
  }

  public setAccountBalanceHistory(value: any): void {
    this.accountBalanceHistory$.next(value);
  }

  public getAccountBalanceHistory(): any {
    return this.accountBalanceHistory$.getValue();
  }

  public getAccountBalanceHistoryChanges(): Observable<any> {
    return this.accountBalanceHistory$.asObservable();
  }

  public setAccountTemplate(value: any): void {
    this.accountTemplate$.next(value);
  }

  public getAccountTemplate(): any {
    return this.accountTemplate$.getValue();
  }

  public getAccountTemplateChanges(): Observable<any> {
    return this.accountTemplate$.asObservable();
  }

  public setMergeAccountData(value: any): void {
    this.mergeAccountData$.next(value);
  }

  public getMergeAccountData(): any {
    return this.mergeAccountData$.getValue();
  }

  public getMergeAccountDataChanges(): Observable<any> {
    return this.mergeAccountData$.asObservable();
  }

  public setAccountCaseOwner(caseOwner: string): void {
    this.accountCaseOwner$.next(caseOwner);
  }
  public getAccountCaseOwner(): string {
    return this.accountCaseOwner$.getValue();
  }
  public getAccountCaseOwnerChanges(): Observable<string> {
    return this.accountCaseOwner$.asObservable();
  }

  public setGroupExposureId(value: number): void {
    this.groupExposureId$.next(value);
  }

  public getGroupExposureId(): number {
    return this.groupExposureId$.getValue();
  }

  public getSystemDate(): number {
    return this.systemDate$.getValue();
  }

  public setSystemDate(value: Date): void {
    this.systemDate$.next(value);
  }

  public getSystemDateChanges(): Observable<Date> {
    return this.systemDate$.asObservable();
  }

  public setAccountTreeNodes(value: any): void {
    this.accountTreeNodes$.next(value);
  }

  public getAccountTreeNodes(): any {
    return this.accountTreeNodes$.getValue();
  }

  public getAccountTreeNodesChanges(): Observable<any> {
    return this.accountTreeNodes$.asObservable();
  }

  public setLegalTitleHolder(value: string): void {
    this.legalTitleHolder$.next(value);
  }

  public getLegalTitleHolder(): Observable<string> {
    return this.legalTitleHolder$.asObservable();
  }

  public setAccountCustomers(customers: Item[]): void {
    this.accountCustomers$.next(customers);
  }

  public getAccountCustomers(): Item[] {
    return this.accountCustomers$.getValue();
  }

  public getAccountCustomersChanges(): Observable<any> {
    return this.accountCustomers$.asObservable();
  }

  public setCustomerRepresentatives(customers: Item[]): void {
    this.customerRepresentatives$.next(customers);
  }

  public getCustomerRepresentatives(): Item[] {
    return this.customerRepresentatives$.getValue();
  }

  public getCustomerRepresentativesChanges(): Observable<any> {
    return this.customerRepresentatives$.asObservable();
  }

  public filterActiveRepresentativeParties(parties: Item[]): Item[] {
    const representatives = this.getCustomerRepresentatives().filter(
      (el) => el.fields.Active
    );
    return representatives.map((representative) =>
      parties.find(
        (party) => party.id === representative.fields?.AssociatedParty
      )
    );
  }

  public setAccountRepresentatives(customers: Item[]): void {
    this.accountRepresentatives$.next(customers);
  }

  public getAccountRepresentatives(): Item[] {
    return this.accountRepresentatives$.getValue();
  }

  public getAccountRepresentativesChanges(): Observable<any> {
    return this.accountRepresentatives$.asObservable();
  }

  public setAccountParticipants(customers: Item[]): void {
    this.accountParticipants$.next(customers);
  }

  public getAccountParticipants(): Item[] {
    return this.accountParticipants$.getValue();
  }

  public getAccountParticipantsChanges(): Observable<any> {
    return this.accountParticipants$.asObservable();
  }

  public setCustomerRepresentativeParties(customers: Item[]): void {
    this.customerRepresentativeParties$.next(customers);
  }

  public getCustomerRepresentativeParties(): Item[] {
    return this.customerRepresentativeParties$.getValue();
  }

  public getCustomerRepresentativePartiesChanges(): Observable<any> {
    return this.customerRepresentativeParties$.asObservable();
  }

  public setAccountRepresentativeParties(customers: Item[]): void {
    this.accountRepresentativeParties$.next(customers);
  }

  public getAccountRepresentativeParties(): Item[] {
    return this.accountRepresentativeParties$.getValue();
  }

  public getAccountRepresentativePartiesChanges(): Observable<any> {
    return this.accountRepresentativeParties$.asObservable();
  }

  public setAccountParticipantParties(customers: Item[]): void {
    this.accountParticipantParties$.next(customers);
  }

  public getAccountParticipantParties(): Item[] {
    return this.accountParticipantParties$.getValue();
  }

  public getAccountParticipantPartiesChanges(): Observable<any> {
    return this.accountParticipantParties$.asObservable();
  }

  public setAccountRelatedCustomers(customers: Item[]): void {
    this.accountRelatedCustomers$.next(customers);
  }

  public getAccountRelatedCustomers(): Item[] {
    return this.accountRelatedCustomers$.getValue();
  }

  public getAccountRelatedCustomersChanges(): Observable<any> {
    return this.accountRelatedCustomers$.asObservable();
  }

  public setAccountAssets(customers: Item[]): void {
    this.accountAssets$.next(customers);
  }

  public getAccountAssets(): Item[] {
    return this.accountAssets$.getValue();
  }

  public getAccountAssetsChanges(): Observable<any> {
    return this.accountAssets$.asObservable();
  }

  public async getAssets(accountId): Promise<void> {
    await this.dynamicApi
      .searchItemByIds('SecuritySearch', [accountId], {})
      .then(async (response: any) => {
        const assets = await response
          .filter((element) => element.fields.Type !== 'VehicleAsset')
          .sort((a, b) => (a.fields.StatusDate < b.fields.StatusDate ? -1 : 1));
        this.setAccountAssets(assets);
      })
      .catch((errorResponse) => {
        this.apiExceptionsHandlerService.defaultErrorToasts(
          errorResponse,
          'dynamic-model'
        );
      });
  }

  public setCompanyId(value: number): void {
    this.companyId$.next(value);
  }

  public getCompanyId(): Observable<number> {
    return this.companyId$.asObservable();
  }
  public getCompanyIdValue(): number {
    return this.companyId$.getValue();
  }

  public setAccountInBreathingSpace(accountInBreathingSpace: boolean): void {
    this.accountInBreathingSpace$.next(accountInBreathingSpace);
  }

  public getAccountInBreathingSpace(): boolean {
    return this.accountInBreathingSpace$.getValue();
  }

  public getAccountInBreathingSpaceChanges(): Observable<any> {
    return this.accountInBreathingSpace$.asObservable();
  }

  public async getBreathingSpaceRecords(accountId): Promise<void> {
    let context = {
      parent: 'Account',
      parentId: accountId,
      parentFieldName: 'BreathingSpace',
    };

    await this.dynamicApi
      .getItems(context)
      .then(async (response: any) => {
        const activeBreathingSpace = await response.some(
          (element) => element.fields.Status === 'Active'
        );
        this.setAccountInBreathingSpace(activeBreathingSpace);
      })
      .catch((errorResponse) => {
        this.apiExceptionsHandlerService.defaultErrorToasts(
          errorResponse,
          'dynamic-model'
        );
      });
  }
}
